<script lang="ts">
  import { env } from '$env/dynamic/public';
  import { onMount } from 'svelte';
  import type { Component } from 'svelte';
  import type { ZodError, ZodSchema } from 'zod';
  import type { SanityReference, SanityDocument } from '$lib/sanity/schemas';
  import * as Schemas from '$lib/sanity/schemas';
  import * as NewsComponents from './News';
  import * as ChartComponents from './Charts';
  import * as CommonComponents from './Common';
  import * as PageComponents from './Pages';
  import { referencesStore } from '$lib/stores/references';
  import { ComponentType, EntityType } from '$lib/sanity/types';
  import type { ContentSourceMap, StudioPathLike } from '@sanity/svelte-loader';
  import GlobalNav from '$components/GlobalNav/GlobalNav.svelte';
  import { captureException } from '@sentry/sveltekit';
  import { isPreview } from '$lib/stores/preview';
  import { page } from '$app/state';

  type UnresolvedDocument = SanityDocument | SanityReference;

  type ComponentPreflightError =
    | { reason: 'unknown_component' }
    | { reason: 'unknown_component_type'; data: SanityDocument }
    | { reason: 'invalid_data'; data: SanityDocument; zodError: ZodError };

  type ComponentPreflightResult =
    | { ok: false; error: ComponentPreflightError }
    | {
        ok: true;
        component: Component;
        data: unknown;
      };

  type RegistryItem = {
    schema: ZodSchema;
    component: Component<any>;
  };

  type Registry = Record<string, RegistryItem>;

  const registry: Registry = {
    [ComponentType.Accordion]: {
      schema: Schemas.AccordionSchema,
      component: CommonComponents.Accordion
    },
    [ComponentType.AccordionItem]: {
      schema: Schemas.AccordionItemSchema,
      component: CommonComponents.AccordionItem
    },
    [ComponentType.Card]: {
      schema: Schemas.CardSchema,
      component: PageComponents.Card
    },
    [ComponentType.ShapeLink]: {
      schema: Schemas.ShapeLinkSchema,
      component: CommonComponents.ShapeLink
    },
    [ComponentType.ShapeLinkGroup]: {
      schema: Schemas.ShapeLinkGroupSchema,
      component: CommonComponents.ShapeLinkGroup
    },
    [ComponentType.Button]: {
      schema: Schemas.ButtonSchema,
      component: CommonComponents.Button
    },
    [ComponentType.Text]: {
      schema: Schemas.TextSchema,
      component: CommonComponents.Text
    },
    [ComponentType.ModalText]: {
      schema: Schemas.ModalTextSchema,
      component: CommonComponents.Text
    },
    [ComponentType.Image]: {
      schema: Schemas.ImageSchema,
      component: CommonComponents.Image
    },
    [ComponentType.Video]: {
      schema: Schemas.VideoSchema,
      component: CommonComponents.Video
    },
    [ComponentType.Media]: {
      schema: Schemas.MediaSchema,
      component: CommonComponents.Media
    },
    [ComponentType.Carousel]: {
      schema: Schemas.CarouselSchema,
      component: CommonComponents.Carousel
    },
    [ComponentType.IconStack]: {
      schema: Schemas.IconStackSchema,
      component: CommonComponents.IconStack
    },
    [ComponentType.Quote]: {
      schema: Schemas.QuoteSchema,
      component: CommonComponents.Quote
    },
    [ComponentType.VanityNumber]: {
      schema: Schemas.VanityNumberSchema,
      component: CommonComponents.VanityNumber
    },
    [ComponentType.NewsSection]: {
      schema: Schemas.NewsSectionSchema,
      component: NewsComponents.NewsSection
    },
    [ComponentType.NewsCard]: {
      schema: Schemas.NewsCardSchema,
      component: NewsComponents.NewsCard
    },
    [ComponentType.NewsLatestPosts]: {
      schema: Schemas.NewsLatestPostsSchema,
      component: NewsComponents.NewsLatestPosts
    },
    [ComponentType.NewsRunningLane]: {
      schema: Schemas.NewsRunningLaneSchema,
      component: NewsComponents.NewsRunningLane
    },
    [ComponentType.PageGallery]: {
      schema: Schemas.PageGallerySchema,
      component: PageComponents.Gallery
    },
    [ComponentType.PageHero]: {
      schema: Schemas.PageHeroSchema,
      component: PageComponents.Hero
    },
    [ComponentType.PageSection]: {
      schema: Schemas.PageSectionSchema,
      component: PageComponents.Section
    },
    [ComponentType.PageFullWidthMedia]: {
      schema: Schemas.PageFullWidthMediaSchema,
      component: PageComponents.FullWidthMedia
    },
    [ComponentType.Bar]: {
      schema: Schemas.BarSchema,
      component: ChartComponents.Bar
    },
    [ComponentType.BarGroup]: {
      schema: Schemas.BarGroupSchema,
      component: ChartComponents.BarGroup
    },
    [ComponentType.Globe]: {
      schema: Schemas.GlobeSchema,
      component: ChartComponents.Globe
    },
    [ComponentType.Map]: {
      schema: Schemas.MapSchema,
      component: ChartComponents.Map
    },
    [ComponentType.Pie]: {
      schema: Schemas.PieSchema,
      component: ChartComponents.Pie
    },
    [ComponentType.StackedBar]: {
      schema: Schemas.StackedBarSchema,
      component: ChartComponents.StackedBar
    },
    [ComponentType.StackedBarGroup]: {
      schema: Schemas.StackedBarGroupSchema,
      component: ChartComponents.StackedBarGroup
    },
    [ComponentType.DatawrapperChart]: {
      schema: Schemas.DatawrapperChartSchema,
      component: ChartComponents.DatawrapperChart
    },
    [ComponentType.Treemap]: {
      schema: Schemas.TreemapSchema,
      component: ChartComponents.Treemap
    },
    [EntityType.Navigation]: {
      schema: Schemas.NavigationSchema,
      component: GlobalNav
    }
  };

  const componentPreflightCheck = (doc: UnresolvedDocument): ComponentPreflightResult => {
    const resolvedDocument = referencesStore.dereference<SanityDocument>(doc);

    if (!resolvedDocument) {
      if (isProdEnv) {
        captureException(resolvedDocument, {
          tags: { source: 'SanityComponentFactory' },
          extra: { reason: 'unknown_component' }
        });
      }

      return { ok: false, error: { reason: 'unknown_component' } };
    }

    if (!Object.keys(registry).includes(resolvedDocument._type)) {
      if (isProdEnv) {
        captureException(resolvedDocument, {
          tags: { source: 'SanityComponentFactory' },
          extra: { reason: 'unknown_component_type', data: resolvedDocument }
        });
      }

      return { ok: false, error: { reason: 'unknown_component_type', data: resolvedDocument } };
    }

    const componentType = resolvedDocument._type as keyof typeof registry;
    const { component, schema } = registry[componentType];

    const result = schema.safeParse(resolvedDocument);
    if (!result.success) {
      if (isProdEnv) {
        captureException(result, {
          tags: { source: 'SanityComponentFactory' },
          extra: { reason: 'invalid_data', zodError: result.error, data: resolvedDocument }
        });
      }

      return {
        ok: false,
        error: { reason: 'invalid_data', zodError: result.error, data: resolvedDocument }
      };
    }

    return {
      ok: true,
      data: result.data,
      component
    } as ComponentPreflightResult;
  };

  let {
    document,
    sourceMap = undefined,
    path = [],
    gridColspan = undefined
  }: {
    document: UnresolvedDocument;
    sourceMap?: ContentSourceMap;
    path?: StudioPathLike;
    gridColspan?: number;
  } = $props();

  let isProdEnv = $state(false);
  let preflightResult: ComponentPreflightResult = $derived(componentPreflightCheck(document));

  onMount(() => {
    const environmentValue = page.data.testEnv
      ? window?.MOCK_TEST_ENV || 'development'
      : env.PUBLIC_ENVIRONMENT || 'development';

    isProdEnv = environmentValue === 'production';
  });
</script>

{#if preflightResult.ok}
  <preflightResult.component document={preflightResult.data} {sourceMap} {path} {gridColspan} />
{:else if preflightResult.error.reason === 'unknown_component_type' && (!isProdEnv || $isPreview)}
  <div class="error">
    <h1>Error: Unknown component</h1>
    <p>
      Unknown component of type <b>"{preflightResult.error.data._type}"</b> was passed to component factory.
    </p>
  </div>
{:else if preflightResult.error.reason === 'invalid_data' && (!isProdEnv || $isPreview)}
  <div class="error">
    <h1>Error: Invalid data for "{preflightResult.error.data._type}" component</h1>
    <p>Validating component data with a zod schema produced the following errors:</p>
    <pre>{preflightResult.error.zodError.message}</pre>
  </div>
{/if}

<style lang="scss">
  .error {
    padding: 24px;
    color: white;
    background: black;
    font-family: var(--font-family-primary);
  }

  h1 {
    margin-bottom: 12px;
  }
</style>
